
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../src/libs/supabase';
import { sendEmail } from '../../src/libs/sendEmail';
import { Reveal } from '../Reveal';
import { NeuralChat } from '../NeuralChat';
import {
  Check, User, Puzzle, Calendar, Zap, Search, FileText, PenTool, BarChart3,
  MessageSquare, Briefcase, ArrowRight, Linkedin, ShieldCheck, Loader2, Plus, Minus,
  CheckCircle2, Banknote, Cpu, School, GraduationCap, FileCheck
} from 'lucide-react';
import { Page } from '../../types';

interface TrainingPageProps {
  onNavigate?: (page: Page) => void;
}

// --- SCHEMA.ORG JSON-LD ---
const SchemaMarkup = () => {
  const { t } = useTranslation();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": ["EducationalOrganization", "Course"],
    "name": "CleverDigital - KI Schulungen & Workshops", // Keep static or add specific schema keys if needed
    "description": t('shared.training.hero.subtitle'),
    "provider": {
      "@type": "Organization",
      "name": "CleverDigital",
      "url": "https://cleverdigital.at/"
    },
    "courseMode": "Online",
    "educationalCredentialAwarded": "Teilnahmebestätigung",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Online",
      "location": "Virtual"
    },
    "teaches": ["Prompt Engineering", "ChatGPT im Business", "KI Tools", "Prozessautomatisierung"]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
};

// --- FAQ COMPONENT (Matching Home Style) ---
const FAQSection = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = t('shared.training.faq.items', { returnObjects: true }) as Array<{ q: string; a: string }>;

  return (
    <div className="flex flex-col border-t border-gray-200">
      {faqs.map((faq, index) => (
        <Reveal key={index} delay={index * 0.1}>
          <div className={`group border-b border-gray-200 transition-colors duration-300 ${openIndex === index ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
            <button
              className="w-full flex items-start md:items-center justify-between py-10 px-4 md:px-8 text-left"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-12">
                <span className="font-mono text-xs text-gray-400 uppercase tracking-widest">
                  // TR_{String(index + 1).padStart(2, '0')}
                </span>
                <span className={`text-xl md:text-2xl font-bold transition-colors duration-300 ${openIndex === index ? 'text-clever-teal' : 'text-gray-900'}`}>
                  {faq.q}
                </span>
              </div>

              <div className="ml-4 flex-shrink-0 text-gray-400 font-mono">
                {openIndex === index ? <Minus size={20} /> : <Plus size={20} />}
              </div>
            </button>

            <div
              className={`overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${openIndex === index ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}
            >
              <div className="px-4 md:px-8 pb-10 grid grid-cols-1 md:grid-cols-12">
                <div className="hidden md:block md:col-span-2"></div>
                <div className="md:col-span-8">
                  <div className="text-lg leading-relaxed text-gray-600 font-light pl-4 border-l border-clever-teal/30">
                    {faq.a}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
};

export const TrainingPage: React.FC<TrainingPageProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    employees: '',
    format: '',
    message: '',
    dsgvo: false
  });

  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const { error } = await supabase.from('leads').insert([{
        company: formData.company,
        email: formData.email,
        sector: 'Training Request',
        details: `Name: ${formData.firstName} ${formData.lastName}\nFormat: ${formData.format}\nParticipants: ${formData.employees}\nMessage: ${formData.message}`
      }]);

      if (error) throw error;

      sendEmail({
        email: formData.email,
        name: `${formData.firstName} ${formData.lastName}`,
        subject: 'New AI Training Request',
        message: `New Training Request:\nName: ${formData.firstName} ${formData.lastName}\nCompany: ${formData.company}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nParticipants: ${formData.employees}\nFormat: ${formData.format}\n\nMessage:\n${formData.message}`,
        lang: i18n.language
      }).catch(console.error);

      setFormStatus('success');
      setFormData({
        firstName: '', lastName: '', email: '', phone: '', company: '',
        employees: '', format: '', message: '', dsgvo: false
      });
    } catch (err) {
      console.error(err);
      alert(t('common.form_error'));
      setFormStatus('idle');
    }
  };

  const scrollToForm = () => {
    const element = document.getElementById('training-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-white min-h-screen font-sans text-gray-900 selection:bg-clever-teal selection:text-white">
      <SchemaMarkup />

      {/* 1. HERO SECTION (Home Style) */}
      <section className="relative pt-40 pb-40 overflow-hidden flex flex-col justify-start items-center bg-white border-b border-gray-100">
        {/* Grid Background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_50%_50%,transparent,white)] pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-6 relative z-10 w-full">
          <div className="flex flex-col items-center text-center">
            <Reveal>
              <div className="inline-flex items-center gap-3 mb-10 opacity-0 animate-[fadeIn_1s_ease-out_forwards]">
                <span className="h-px w-8 bg-gray-300"></span>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-gray-400">
                  {t('shared.training.hero.badge')}
                </span>
                <span className="h-px w-8 bg-gray-300"></span>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-gray-900 mb-8 leading-[0.95]">
                {t('shared.training.hero.title_line1')} <br />
                <span className="text-gray-900">{t('shared.training.hero.title_line2')}</span>
              </h1>
            </Reveal>

            <Reveal delay={0.2}>
              <p className="text-xl text-gray-500 max-w-xl mx-auto mb-4 font-light leading-relaxed">
                {t('shared.training.hero.subtitle')}
              </p>
            </Reveal>

            <Reveal delay={0.3} width="100%">
              <div className="w-full flex justify-center mt-20">
                <NeuralChat />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. INTRO TEXT (White) */}
      <section className="py-32 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('shared.training.intro.title_line1')}<br />{t('shared.training.intro.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-500 text-lg max-w-md">
                  {t('shared.training.intro.subtitle')}
                </p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div className="text-lg text-gray-600 leading-relaxed">
                <p className="mb-6">
                  {t('shared.training.intro.p1')}
                </p>
                <p>
                  {t('shared.training.intro.p2')}
                </p>
              </div>

              <div className="bg-gray-50 p-10 border-l-4 border-clever-teal">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-6">{t('shared.training.intro.list_title')}</h3>
                <ul className="space-y-4">
                  {(t('shared.training.intro.items', { returnObjects: true }) as string[]).map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="text-clever-teal shrink-0 mt-1" size={18} />
                      <span className="text-gray-800 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3. FORMATS (Grid Style like Benefits) */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('shared.training.formats.title_line1')}<br />{t('shared.training.formats.title_line2')}
              </h2>
              <p className="text-gray-500 max-w-md text-right mt-8 md:mt-0 text-lg">
                {t('shared.training.formats.subtitle')}
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-gray-200">
            {[
              {
                id: "01",
                icon: <User size={32} />,
                title: t('shared.training.formats.items.basic.title'),
                desc: t('shared.training.formats.items.basic.desc'),
                tag: t('shared.training.formats.items.basic.tag')
              },
              {
                id: "02",
                icon: <Puzzle size={32} />,
                title: t('shared.training.formats.items.custom.title'),
                desc: t('shared.training.formats.items.custom.desc'),
                tag: t('shared.training.formats.items.custom.tag')
              },
              {
                id: "03",
                icon: <Calendar size={32} />,
                title: t('shared.training.formats.items.sustain.title'),
                desc: t('shared.training.formats.items.sustain.desc'),
                tag: t('shared.training.formats.items.sustain.tag')
              }
            ].map((item, idx) => (
              <Reveal key={idx} delay={idx * 0.1}>
                <div className="group p-12 border-b border-r border-gray-200 hover:bg-gray-50 transition-all duration-500 min-h-[400px] flex flex-col justify-between relative overflow-hidden">

                  <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <span className="text-clever-teal text-xs font-bold uppercase tracking-widest">{item.tag}</span>
                  </div>

                  <div className="text-xs font-mono text-gray-400 mb-8">
                     // FORMAT_0{item.id}
                  </div>

                  <div>
                    <div className="text-clever-teal mb-6">{item.icon}</div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-6 group-hover:translate-x-2 transition-transform duration-300">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 leading-relaxed text-lg">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. CURRICULUM (List Style) */}
      <section className="py-32 bg-[#0B1121] text-white border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-800 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter leading-none">
                {t('shared.training.curriculum.title_line1')}<br />{t('shared.training.curriculum.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-400 text-lg max-w-md">
                  {t('shared.training.curriculum.subtitle')}
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
              {[
                { title: t('shared.training.curriculum.items.prompting.title'), icon: <Zap size={24} />, text: t('shared.training.curriculum.items.prompting.text') },
                { title: t('shared.training.curriculum.items.llms.title'), icon: <MessageSquare size={24} />, text: t('shared.training.curriculum.items.llms.text') },
                { title: t('shared.training.curriculum.items.email.title'), icon: <Briefcase size={24} />, text: t('shared.training.curriculum.items.email.text') },
                { title: t('shared.training.curriculum.items.meetings.title'), icon: <FileText size={24} />, text: t('shared.training.curriculum.items.meetings.text') },
                { title: t('shared.training.curriculum.items.docs.title'), icon: <Search size={24} />, text: t('shared.training.curriculum.items.docs.text') },
                { title: t('shared.training.curriculum.items.content.title'), icon: <PenTool size={24} />, text: t('shared.training.curriculum.items.content.text') },
                { title: t('shared.training.curriculum.items.data.title'), icon: <BarChart3 size={24} />, text: t('shared.training.curriculum.items.data.text') },
                { title: t('shared.training.curriculum.items.tools.title'), icon: <Cpu size={24} />, text: t('shared.training.curriculum.items.tools.text') },
                { title: t('shared.training.curriculum.items.specific.title'), icon: <User size={24} />, text: t('shared.training.curriculum.items.specific.text') },
              ].map((mod, i) => (
                <div key={i} className="group">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-white/5 rounded-lg text-clever-teal border border-clever-teal/20 group-hover:bg-clever-teal group-hover:text-white transition-colors">
                      {mod.icon}
                    </div>
                    <h4 className="text-xl font-bold text-white">{mod.title}</h4>
                  </div>
                  <p className="text-gray-400 leading-relaxed text-sm pl-[60px]">
                    {mod.text}
                  </p>
                </div>
              ))}
            </div>

            {/* NEW CTA BUTTON */}
            <div className="mt-24 text-center">
              <button
                onClick={scrollToForm}
                className="bg-clever-teal text-white px-10 py-4 font-bold uppercase tracking-widest text-xs hover:bg-red-700 transition-all shadow-lg hover:shadow-red-900/20 inline-flex items-center gap-3 rounded-sm"
              >
                {t('shared.training.curriculum.cta')} <ArrowRight size={16} />
              </button>
            </div>

          </Reveal>
        </div>
      </section>

      {/* 5. AGENDA (Timeline Style) */}
      <section className="py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-24">
              <div className="inline-block px-4 py-2 bg-gray-100 text-gray-900 text-xs font-bold uppercase tracking-[0.2em] mb-8 border border-gray-200">
                {t('shared.training.agenda.badge')}
              </div>
              <h2 className="text-4xl md:text-6xl font-bold text-gray-900 tracking-tighter">{t('shared.training.agenda.title')}</h2>
            </div>

            <div className="relative">
              <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-gray-300 -translate-x-1/2"></div>

              <div className="space-y-24">
                {(t('shared.training.agenda.items', { returnObjects: true }) as Array<{ time: string, title: string, desc: string }>).map((step, index) => (
                  <Reveal key={index} delay={index * 0.1}>
                    <div className={`flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-0 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>

                      <div className={`md:w-1/2 ${index % 2 !== 0 ? 'md:pl-24' : 'md:pr-24'} pl-16 w-full`}>
                        <div className="relative">
                          <span className="text-xs font-bold uppercase tracking-widest text-clever-teal mb-2 block">{step.time}</span>
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h3>
                          <p className="text-gray-500 leading-relaxed">{step.desc}</p>
                        </div>
                      </div>

                      <div className="absolute left-[19px] md:left-1/2 -translate-x-1/2 flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-clever-teal ring-8 ring-white z-10"></div>
                      </div>

                      <div className="hidden md:block md:w-1/2" />

                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. TRAINER (Dark) */}
      <section className="py-32 bg-[#0B1121] text-white border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <div className="inline-block text-clever-teal text-xs font-bold uppercase tracking-[0.2em] mb-8">
              {t('shared.training.trainer.badge')}
            </div>
            <div className="flex flex-col md:flex-row gap-12 items-start">
              <div className="w-40 h-40 rounded-full flex-shrink-0 border-2 border-clever-teal flex items-center justify-center shadow-[0_0_30px_rgba(20,184,166,0.3)] overflow-hidden">
                <img src="/assets/dmitry-pashlov.jpg" alt={t('shared.training.trainer.title')} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <h3 className="text-4xl font-bold text-white mb-2">{t('shared.training.trainer.title')}</h3>
                <p className="text-gray-400 text-sm uppercase tracking-widest mb-8 font-mono">{t('shared.training.trainer.role')}</p>

                <div className="space-y-6 text-lg text-gray-300 font-light leading-relaxed mb-8 border-l-2 border-gray-700 pl-6">
                  <p>
                    {t('shared.training.trainer.bio1')}
                  </p>
                  <p>
                    {t('shared.training.trainer.bio2')}
                  </p>
                </div>

                <a href="https://www.linkedin.com/in/dmitrypashlov/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-white hover:text-clever-teal transition-colors font-bold border-b-2 border-transparent hover:border-clever-teal pb-1">
                  <Linkedin size={20} /> {t('shared.training.trainer.linkedin')}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 7. AMS FÖRDERUNG (RE-DESIGNED: DARK / GRID / INFORMATIVE) */}
      <section className="py-32 bg-[#0B1121] text-white border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-700 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter leading-none">
                {t('shared.training.ams.title_line1')}<br />{t('shared.training.ams.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-400 text-lg max-w-md">
                  {t('shared.training.ams.subtitle')}
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 border-t border-l border-gray-700">
              {/* Column 1: The Grant */}
              <div className="p-10 border-b border-r border-gray-700 hover:bg-white/5 transition-all">
                <GraduationCap className="text-clever-teal mb-6" size={32} />
                <h3 className="text-xl font-bold text-white mb-4">{t('shared.training.ams.items.grant.title')}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  {t('shared.training.ams.items.grant.desc')}
                </p>
                <a
                  href="https://www.ams.at/unternehmen/service-zur-personalsuche/foerderungen/qualifizierungsfoerderung-fuer-beschaeftigte"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-clever-teal text-xs font-bold uppercase tracking-widest hover:text-white transition-colors"
                >
                  {t('shared.training.ams.items.grant.cta')} <ArrowRight size={14} />
                </a>
              </div>

              {/* Column 2: The Amount */}
              <div className="p-10 border-b border-r border-gray-700 hover:bg-white/5 transition-all">
                <Banknote className="text-clever-teal mb-6" size={32} />
                <h3 className="text-xl font-bold text-white mb-4">{t('shared.training.ams.items.amount.title')}</h3>
                <div className="text-6xl font-bold text-white mb-2 tracking-tighter">50%</div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {t('shared.training.ams.items.amount.desc')}
                </p>
              </div>

              {/* Column 3: Our Service */}
              <div className="p-10 border-b border-r border-gray-700 hover:bg-white/5 transition-all relative overflow-hidden">
                <div className="absolute top-0 right-0 p-2">
                  <div className="w-2 h-2 bg-clever-teal rounded-full animate-pulse"></div>
                </div>
                <FileCheck className="text-clever-teal mb-6" size={32} />
                <h3 className="text-xl font-bold text-white mb-4">{t('shared.training.ams.items.support.title')}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  {t('shared.training.ams.items.support.desc')}
                </p>
                <div className="bg-white/10 p-3 rounded border-l-2 border-clever-teal text-xs text-gray-300 italic">
                  {t('shared.training.ams.items.support.note')}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 8. FAQ */}
      <section className="py-32 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('shared.training.faq.title_line1')}<br />{t('shared.training.faq.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-500 text-lg max-w-md">
                  {t('shared.training.faq.subtitle')}
                </p>
              </div>
            </div>
            <FAQSection />
          </Reveal>
        </div>
      </section>

      {/* 9. FORM (Navy) */}
      <section id="training-form" className="py-32 bg-[#0B1121] relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <Reveal>
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tighter">{t('shared.training.form.title')}</h2>
              <p className="text-gray-400 text-lg">{t('shared.training.form.subtitle')}</p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            {formStatus === 'success' ? (
              <div className="bg-white p-12 text-center shadow-2xl border border-gray-100 rounded-none flex flex-col items-center">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle2 size={40} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('shared.training.form.success.title')}</h3>
                <p className="text-gray-600 mb-8">{t('shared.training.form.success.desc')}</p>
                <button onClick={() => setFormStatus('idle')} className="text-clever-teal font-bold underline">{t('shared.training.form.success.cta')}</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 shadow-2xl border border-gray-100 rounded-none">
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('shared.training.form.labels.firstName')}</label>
                    <input required className="w-full bg-gray-50 border-b border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors placeholder-gray-300" placeholder={t('shared.training.form.placeholders.firstName')} value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('shared.training.form.labels.lastName')}</label>
                    <input required className="w-full bg-gray-50 border-b border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors placeholder-gray-300" placeholder={t('shared.training.form.placeholders.lastName')} value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('shared.training.form.labels.email')}</label>
                    <input required type="email" className="w-full bg-gray-50 border-b border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors placeholder-gray-300" placeholder={t('shared.training.form.placeholders.email')} value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('shared.training.form.labels.phone')}</label>
                    <input required type="tel" className="w-full bg-gray-50 border-b border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors placeholder-gray-300" placeholder={t('shared.training.form.placeholders.phone')} value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                </div>

                <div className="space-y-2 mb-8">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('shared.training.form.labels.company')}</label>
                  <input required className="w-full bg-gray-50 border-b border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors placeholder-gray-300" placeholder={t('shared.training.form.placeholders.company')} value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} />
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('shared.training.form.labels.employees')}</label>
                    <select className="w-full bg-gray-50 border-b border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors appearance-none rounded-none" value={formData.employees} onChange={e => setFormData({ ...formData, employees: e.target.value })}>
                      <option value="">{t('shared.training.form.placeholders.employees')}</option>
                      {(t('shared.training.form.options.employees', { returnObjects: true }) as Array<{ value: string, label: string }>).map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('shared.training.form.labels.format')}</label>
                    <select className="w-full bg-gray-50 border-b border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors appearance-none rounded-none" value={formData.format} onChange={e => setFormData({ ...formData, format: e.target.value })}>
                      <option value="">{t('shared.training.form.placeholders.format')}</option>
                      {(t('shared.training.form.options.formats', { returnObjects: true }) as Array<{ value: string, label: string }>).map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2 mb-8">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('shared.training.form.labels.message')}</label>
                  <textarea className="w-full bg-gray-50 border border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors resize-none h-32 placeholder-gray-300" placeholder={t('shared.training.form.placeholders.message')} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} />
                </div>

                <div className="mb-8">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input required type="checkbox" className="mt-1 w-5 h-5 border-gray-300 text-clever-teal focus:ring-clever-teal" checked={formData.dsgvo} onChange={e => setFormData({ ...formData, dsgvo: e.target.checked })} />
                    <span className="text-sm text-gray-500 group-hover:text-gray-700">{t('shared.training.form.labels.dsgvo')}</span>
                  </label>
                </div>

                <button type="submit" disabled={formStatus === 'submitting'} className="w-full bg-clever-teal text-white py-6 font-bold uppercase tracking-widest hover:bg-red-700 transition-all shadow-lg flex items-center justify-center gap-3 disabled:opacity-70 rounded-sm">
                  {formStatus === 'submitting' ? <><Loader2 className="animate-spin" /> {t('shared.website.modal.form.submitting')}</> : <>{t('shared.training.curriculum.cta')} <ArrowRight /></>}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>

    </div>
  );
};
